<template>
    <div class="guide-page">
        <!-- 背景图片 -->
        <img class="bg-img" src="/static/t2.png" @load="onBgLoad" alt="" />
        <div class="overlay" :class="{ loaded: bgLoaded }"></div>

        <!-- 步骤内容区域 -->
        <div class="guide-content">
            <!-- 步骤1：欢迎 -->
            <div class="step-panel" v-if="currentStep === 1">
                <div class="welcome-icon">👋</div>
                <div class="step-title">
                    <span class="title-main">你好呀！我是小酒窝~</span>
                    <span class="title-sub">你的专属心理陪伴师</span>
                    <span class="title-desc">有什么心事都可以跟我说，我会认真倾听并帮你分析</span>
                </div>
            </div>

            <!-- 步骤2：学段选择 -->
            <div class="step-panel" v-if="currentStep === 2">
                <div class="step-icon">🎓</div>
                <div class="step-title">
                    <span class="title-main">你现在是哪个阶段呀？</span>
                    <span class="title-sub">不同阶段的你，烦恼可能不太一样</span>
                </div>
                <div class="age-options">
                    <div
                        class="age-item"
                        :class="{ active: userAge === 'primary' }"
                        @click="selectAge('primary')"
                    >
                        <span class="age-num">小学</span>
                        <span class="age-label">阶段</span>
                    </div>
                    <div
                        class="age-item"
                        :class="{ active: userAge === 'junior' }"
                        @click="selectAge('junior')"
                    >
                        <span class="age-num">初中</span>
                        <span class="age-label">阶段</span>
                    </div>
                    <div
                        class="age-item"
                        :class="{ active: userAge === 'senior' }"
                        @click="selectAge('senior')"
                    >
                        <span class="age-num">高中</span>
                        <span class="age-label">阶段</span>
                    </div>
                </div>
            </div>

            <!-- 步骤3：功能介绍 -->
            <div class="step-panel" v-if="currentStep === 3">
                <div class="step-icon">✨</div>
                <div class="step-title">
                    <span class="title-main">小酒窝可以帮你</span>
                    <span class="title-sub">选择你最需要的帮助（可多选）</span>
                </div>
                <div class="interest-options">
                    <div
                        class="interest-item"
                        :class="{ active: userInterests.includes('chat') }"
                        @click="toggleInterest('chat')"
                    >
                        <span class="interest-icon">💙</span>
                        <span class="interest-label">倾诉心事</span>
                    </div>
                    <div
                        class="interest-item"
                        :class="{ active: userInterests.includes('analysis') }"
                        @click="toggleInterest('analysis')"
                    >
                        <span class="interest-icon">🧠</span>
                        <span class="interest-label">心理分析</span>
                    </div>
                    <div
                        class="interest-item"
                        :class="{ active: userInterests.includes('study') }"
                        @click="toggleInterest('study')"
                    >
                        <span class="interest-icon">📚</span>
                        <span class="interest-label">学习压力</span>
                    </div>
                    <div
                        class="interest-item"
                        :class="{ active: userInterests.includes('growth') }"
                        @click="toggleInterest('growth')"
                    >
                        <span class="interest-icon">🌱</span>
                        <span class="interest-label">自我成长</span>
                    </div>
                </div>
            </div>

            <!-- 步骤4：完成 -->
            <div class="step-panel" v-if="currentStep === 4">
                <div class="complete-animation">
                    <div class="celebration-emoji">🎉</div>
                    <div class="sparkles">
                        <span class="sparkle s1">✨</span>
                        <span class="sparkle s2">⭐</span>
                        <span class="sparkle s3">✨</span>
                    </div>
                </div>
                <div class="step-title">
                    <span class="title-main">准备好啦！</span>
                    <span class="title-sub">小酒窝会用心倾听你的每一句话</span>
                </div>
                <div class="complete-info">
                    <div class="info-item">
                        <span class="info-label">学段：</span>
                        <span class="info-value">{{ ageLabelMap[userAge] || userAge }}</span>
                    </div>
                    <div class="info-item">
                        <span class="info-label">关注：</span>
                        <span class="info-value">
                            {{ userInterests.length > 0 ? interestLabels : '倾诉心事、心理分析' }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- 底部按钮区域 -->
        <div class="guide-footer">
            <!-- 步骤指示器 -->
            <div class="step-indicators" v-if="currentStep < 4">
                <div
                    class="indicator"
                    v-for="n in 3"
                    :key="n"
                    :class="{ active: n === currentStep, completed: n < currentStep }"
                ></div>
            </div>

            <!-- 操作按钮 -->
            <div class="action-buttons" :class="{ 'center-mode': currentStep === 4 }">
                <span class="btn-prev" v-if="currentStep > 1" @click="handlePrev">← 上一步</span>
                <div
                    class="btn-next"
                    :class="{ disabled: !canProceed, 'btn-start': currentStep === 4 }"
                    @click="handleNext"
                >
                    <span>{{ currentStep === 4 ? '开始探索' : '下一步' }}</span>
                    <span class="btn-arrow" v-if="currentStep < 4">→</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { request } from '@/utils/request.js';
import { getStorage, setStorage } from '@/utils/storage.js';

export default {
    data() {
        return {
            currentStep: 1, // 当前步骤
            userAge: '', // 用户选择的学段（primary/junior/senior）
            userInterests: [], // 用户兴趣
            bgLoaded: false, // 背景图是否加载完成
            // 兴趣标识到中文的映射
            interestMap: {
                chat: '倾诉心事',
                analysis: '心理分析',
                study: '学习压力',
                growth: '自我成长',
            },
            // 学段标识到中文的映射
            ageLabelMap: {
                primary: '小学',
                junior: '初中',
                senior: '高中',
            },
        };
    },
    computed: {
        // 是否可以进入下一步
        canProceed() {
            switch (this.currentStep) {
                case 1:
                    return true; // 欢迎页可以直接下一步
                case 2:
                    return !!this.userAge; // 必须选择学段
                case 3:
                    return this.userInterests.length > 0; // 至少选择一个兴趣
                case 4:
                    return true; // 完成页可以直接开始
                default:
                    return false;
            }
        },
        // 兴趣标签显示
        interestLabels() {
            return this.userInterests.map((i) => this.interestMap[i]).join('、');
        },
    },
    async created() {
        // 已完成引导则直接进入首页
        if (getStorage('hasCompletedGuide')) {
            this.$router.replace('/chat');
        }
    },
    methods: {
        // 背景图加载完成
        onBgLoad() {
            this.bgLoaded = true;
        },
        // 选择学段
        selectAge(age) {
            this.userAge = age;
        },
        // 切换兴趣选择
        toggleInterest(interest) {
            const index = this.userInterests.indexOf(interest);
            if (index > -1) {
                this.userInterests.splice(index, 1);
            } else {
                this.userInterests.push(interest);
            }
        },
        // 处理下一步
        handleNext() {
            if (!this.canProceed) return;

            if (this.currentStep < 4) {
                this.currentStep++;
            } else {
                // 完成引导，保存用户偏好
                this.completeGuide();
            }
        },
        // 返回上一步
        handlePrev() {
            if (this.currentStep > 1) {
                this.currentStep--;
            }
        },
        // 完成引导：缓存偏好并保存到后端，然后进入首页
        async completeGuide() {
            // 转换为中文文字
            const ageGroupLabel = this.ageLabelMap[this.userAge] || this.userAge;
            const interestLabels = this.userInterests
                .map((i) => this.interestMap[i])
                .filter(Boolean);

            // 缓存偏好，供首页发送欢迎语使用
            setStorage('userPreferences', {
                ageGroup: ageGroupLabel,
                interests: interestLabels,
            });

            // 保存偏好到后端
            try {
                const res = await request({
                    url: '/api/user/preferences',
                    method: 'POST',
                    data: {
                        ageGroup: ageGroupLabel,
                        interests: interestLabels,
                    },
                });

                if (res.data.code === 200) {
                    console.log('用户偏好保存成功');
                } else {
                    console.log('保存偏好失败:', res.data.message);
                }
            } catch (e) {
                console.log('保存偏好请求失败:', e);
            }

            // 跳转到首页
            this.$router.replace('/chat');
        },
    },
};
</script>

<style lang="scss" scoped>
.guide-page {
    width: 100vw;
    height: 100vh;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

// 背景图片
.bg-img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    object-fit: cover; // 对应 uniapp image mode="aspectFill"
}

// 遮罩层
.overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.3);
    z-index: 2;
    opacity: 0;
    transition: opacity 0.3s ease;

    &.loaded {
        opacity: 1;
    }
}

// 内容区域
.guide-content {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60rpx 20rpx;
    position: relative;
    z-index: 3;
}

.step-panel {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    animation: fadeInUp 0.5s ease;
}

@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(30rpx);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

// 欢迎图标（步骤1）
.welcome-icon {
    font-size: 120rpx;
    margin-bottom: 40rpx;
    animation: wave 1.5s ease infinite;
}

@keyframes wave {
    0%,
    100% {
        transform: rotate(0deg);
    }
    25% {
        transform: rotate(20deg);
    }
    75% {
        transform: rotate(-10deg);
    }
}

// 步骤图标
.step-icon {
    font-size: 100rpx;
    margin-bottom: 30rpx;
    animation: bounce 1s ease infinite;
}

@keyframes bounce {
    0%,
    100% {
        transform: translateY(0);
    }
    50% {
        transform: translateY(-10rpx);
    }
}

// 标题区域
.step-title {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin-bottom: 50rpx;

    .title-main {
        font-size: 48rpx;
        font-weight: bold;
        color: #ffffff;
        margin-bottom: 20rpx;
        text-shadow: 0 4rpx 10rpx rgba(0, 0, 0, 0.2);
    }

    .title-sub {
        font-size: 28rpx;
        color: rgba(255, 255, 255, 0.8);
        margin-bottom: 16rpx;
    }

    .title-desc {
        font-size: 28rpx;
        color: rgba(255, 255, 255, 0.7);
    }
}

// 年龄选项（原 gap 改为相邻 margin，保证兼容性）
.age-options {
    display: flex;
    justify-content: center;
    width: 100%;

    .age-item {
        width: 160rpx;
        height: 160rpx;
        background: rgba(0, 0, 0, 0.3);
        border-radius: 30rpx;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        border: 4rpx solid transparent;
        transition: all 0.3s ease;

        & + .age-item {
            margin-left: 30rpx;
        }

        &:active {
            transform: scale(0.95);
        }

        &.active {
            background: rgba(255, 255, 255, 0.95);
            border-color: rgba(255, 255, 255, 0.8);
            transform: scale(1.05);

            .age-num {
                color: #333;
            }

            .age-label {
                color: #666;
            }
        }

        .age-num {
            font-size: 40rpx;
            font-weight: bold;
            color: #ffffff;
            margin-bottom: 8rpx;
        }

        .age-label {
            font-size: 24rpx;
            color: rgba(255, 255, 255, 0.8);
        }
    }
}

// 兴趣选项（两列 grid，原 gap 改为 item 外边距实现，保证兼容性）
.interest-options {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    width: 100%;
    max-width: 660rpx;

    .interest-item {
        background: rgba(0, 0, 0, 0.3);
        border-radius: 24rpx;
        padding: 40rpx 30rpx;
        margin: 15rpx; // 30rpx 间距拆分到 item 四周
        display: flex;
        flex-direction: column;
        align-items: center;
        border: 4rpx solid transparent;
        transition: all 0.3s ease;
        box-sizing: border-box;

        &:active {
            transform: scale(0.95);
        }

        &.active {
            background: rgba(255, 255, 255, 0.95);
            border-color: rgba(255, 255, 255, 0.8);

            .interest-icon {
                transform: scale(1.1);
            }

            .interest-label {
                color: #333;
                font-weight: 600;
            }
        }

        .interest-icon {
            font-size: 60rpx;
            margin-bottom: 16rpx;
            transition: transform 0.3s ease;
        }

        .interest-label {
            font-size: 28rpx;
            color: #ffffff;
        }
    }
}

// 完成动画
.complete-animation {
    position: relative;
    margin-bottom: 40rpx;

    .celebration-emoji {
        font-size: 120rpx;
        animation: celebrate 1s ease infinite;
    }

    .sparkles {
        position: absolute;
        top: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 200rpx;
        height: 100%;

        .sparkle {
            position: absolute;
            font-size: 40rpx;
            animation: sparkle 1.5s ease infinite;

            &.s1 {
                top: 0;
                left: 0;
                animation-delay: 0s;
            }

            &.s2 {
                top: 20rpx;
                right: 0;
                animation-delay: 0.3s;
            }

            &.s3 {
                bottom: 0;
                left: 20rpx;
                animation-delay: 0.6s;
            }
        }
    }
}

@keyframes celebrate {
    0%,
    100% {
        transform: scale(1) rotate(0deg);
    }
    25% {
        transform: scale(1.1) rotate(-5deg);
    }
    75% {
        transform: scale(1.1) rotate(5deg);
    }
}

@keyframes sparkle {
    0%,
    100% {
        opacity: 0;
        transform: scale(0);
    }
    50% {
        opacity: 1;
        transform: scale(1);
    }
}

// 完成信息
.complete-info {
    background: rgba(0, 0, 0, 0.3);
    border-radius: 20rpx;
    padding: 30rpx 50rpx;

    .info-item {
        display: flex;
        justify-content: center;
        margin-bottom: 16rpx;

        &:last-child {
            margin-bottom: 0;
        }

        .info-label {
            font-size: 28rpx;
            color: rgba(255, 255, 255, 0.7);
            white-space: nowrap;
        }

        .info-value {
            font-size: 28rpx;
            color: #ffffff;
            font-weight: 600;
            margin-left: 10rpx;
        }
    }
}

// 底部区域
.guide-footer {
    padding: 40rpx 60rpx 60rpx;
    position: relative;
    z-index: 3;
}

// 步骤指示器（原 gap 改为相邻 margin）
.step-indicators {
    display: flex;
    justify-content: center;
    margin-bottom: 40rpx;

    .indicator {
        width: 16rpx;
        height: 16rpx;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.3);
        transition: all 0.3s ease;

        & + .indicator {
            margin-left: 16rpx;
        }

        &.active {
            background: #ffffff;
            transform: scale(1.2);
        }

        &.completed {
            background: rgba(255, 255, 255, 0.7);
        }
    }
}

// 操作按钮
.action-buttons {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &.center-mode {
        justify-content: center;
    }

    .btn-prev {
        font-size: 28rpx;
        color: rgba(255, 255, 255, 0.6);
        padding: 20rpx 30rpx;

        &:active {
            color: rgba(255, 255, 255, 0.9);
        }
    }

    .btn-next {
        margin-left: auto;
        background: rgba(255, 255, 255, 0.9);
        padding: 28rpx 60rpx;
        border-radius: 50rpx;
        display: flex;
        align-items: center;
        box-shadow: 0 10rpx 30rpx rgba(0, 0, 0, 0.2);
        transition: all 0.3s ease;

        &:active {
            transform: scale(0.95);
            background: rgba(255, 255, 255, 1);
        }

        &.disabled {
            opacity: 0.5;
            box-shadow: none;
        }

        // 开始探索按钮美化
        &.btn-start {
            padding: 32rpx 80rpx;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            box-shadow: 0 16rpx 40rpx rgba(102, 126, 234, 0.4);

            &:active {
                transform: scale(0.96);
                box-shadow: 0 8rpx 24rpx rgba(102, 126, 234, 0.3);
            }

            span {
                color: #ffffff;
                font-size: 34rpx;
                letter-spacing: 4rpx;
            }
        }

        span {
            font-size: 32rpx;
            font-weight: bold;
            color: #333;
        }

        .btn-arrow {
            margin-left: 10rpx;
            font-size: 32rpx;
        }
    }
}
</style>
